import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AttributeSection.module.css";

const coerceDateInstance = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      const result = value.toDate();
      return Number.isFinite(result?.getTime()) ? result : null;
    } catch (error) {
      return null;
    }
  }

  if (
    typeof value === "object" &&
    typeof value.seconds === "number" &&
    typeof value.nanoseconds === "number"
  ) {
    const millis =
      value.seconds * 1000 + Math.round(value.nanoseconds / 1e6);
    const result = new Date(millis);
    return Number.isFinite(result.getTime()) ? result : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const parsed = new Date(trimmed);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }

  return null;
};

const formatLocalInputValue = (value) => {
  const date = coerceDateInstance(value);
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatDueDatePreview = (dateTimeString, { locale, t }) => {
  if (!dateTimeString) return "";
  const date = new Date(dateTimeString);
  if (!Number.isFinite(date.getTime())) {
    return "";
  }

  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const timeStr = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

  if (isToday) {
    return t("records.time.task.today_at", {
      time: timeStr,
      defaultValue: `Today at ${timeStr}`,
    });
  }
  if (isTomorrow) {
    return t("records.editor.task.due_preview.tomorrow_at", {
      time: timeStr,
      defaultValue: `Tomorrow at ${timeStr}`,
    });
  }

  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const normalizePriorityValue = (value) => {
  if (typeof value !== "string") return "";
  const normalized = value.trim().toLowerCase();
  return ["high", "medium", "low"].includes(normalized) ? normalized : "";
};

export default function TaskAttributes({
  isComplete,
  dueDate,
  priority,
  onUpdate,
  onDelete,
  aiOverlay = {},
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.resolvedLanguage || i18n?.language;

  const overlayIsComplete =
    aiOverlay?.isComplete && typeof aiOverlay.isComplete === "object"
      ? aiOverlay.isComplete
      : null;
  const overlayDueDate =
    aiOverlay?.dueDate && typeof aiOverlay.dueDate === "object"
      ? aiOverlay.dueDate
      : null;
  const overlayPriority =
    aiOverlay?.priority && typeof aiOverlay.priority === "object"
      ? aiOverlay.priority
      : null;

  const hasOverlay = Boolean(
    overlayIsComplete || overlayDueDate || overlayPriority
  );

  const projectOverlayValue = (currentValue, overlayEntry) => {
    if (!overlayEntry) {
      return currentValue;
    }
    if (overlayEntry.isRemoval) {
      return null;
    }
    return overlayEntry.value;
  };

  const projectedIsComplete = projectOverlayValue(isComplete, overlayIsComplete);
  const projectedDueDate = projectOverlayValue(dueDate, overlayDueDate);
  const projectedPriority = projectOverlayValue(priority, overlayPriority);

  const projectedHasIsComplete =
    projectedIsComplete !== null && projectedIsComplete !== undefined;
  const projectedHasDueDate = Boolean(coerceDateInstance(projectedDueDate));
  const projectedPriorityValue = normalizePriorityValue(projectedPriority);
  const projectedHasPriority = Boolean(projectedPriorityValue);

  const overlayClearsSection =
    hasOverlay &&
    !projectedHasIsComplete &&
    !projectedHasDueDate &&
    !projectedHasPriority;

  const initialDueDate = coerceDateInstance(dueDate);
  const [isDueDateEnabled, setIsDueDateEnabled] = useState(
    () => Boolean(initialDueDate)
  );

  const getSmartDefault = () => {
    const now = new Date();
    const hours = now.getHours();

    if (hours < 17) {
      // Before 5 PM
      const nextHour = new Date(now);
      nextHour.setHours(hours + 1);
      nextHour.setMinutes(Math.ceil(nextHour.getMinutes() / 15) * 15);
      nextHour.setSeconds(0);
      return nextHour;
    } else {
      // After 5 PM
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow;
    }
  };

  // Local datetime value for smoother UX
  const [localDueDateTime, setLocalDueDateTime] = useState(() => {
    if (initialDueDate) {
      return formatLocalInputValue(initialDueDate);
    }
    return formatLocalInputValue(getSmartDefault());
  });

  const overlayDueDateInputValue = useMemo(() => {
    if (!overlayDueDate || overlayDueDate.isRemoval) {
      return "";
    }
    return formatLocalInputValue(overlayDueDate.value);
  }, [overlayDueDate]);

  const dueDateToggleChecked = hasOverlay && overlayDueDate
    ? !overlayDueDate.isRemoval
    : isDueDateEnabled;

  const displayedDueDateTime =
    hasOverlay && overlayDueDate && !overlayDueDate.isRemoval
      ? overlayDueDateInputValue
      : localDueDateTime;

  const dueDatePreviewText = useMemo(() => {
    if (!dueDateToggleChecked) {
      if (hasOverlay && overlayDueDate?.isRemoval) {
        return t("records.editor.task.ai.clear_due_date", "AI will clear the due date.");
      }
      return "";
    }

    const source =
      hasOverlay && overlayDueDate && !overlayDueDate.isRemoval
        ? overlayDueDateInputValue
        : localDueDateTime;

    return formatDueDatePreview(source, { locale, t });
  }, [
    hasOverlay,
    overlayDueDate,
    overlayDueDateInputValue,
    localDueDateTime,
    dueDateToggleChecked,
    locale,
    t,
  ]);

  const displayedPriority =
    hasOverlay && overlayPriority
      ? overlayPriority.isRemoval
        ? ""
        : normalizePriorityValue(overlayPriority.value)
      : normalizePriorityValue(priority);

  const baseIsComplete =
    typeof isComplete === "boolean" ? isComplete : false;

  const displayedIsComplete =
    overlayIsComplete && overlayIsComplete.isRemoval !== true
      ? Boolean(overlayIsComplete.value)
      : overlayIsComplete && overlayIsComplete.isRemoval === true
      ? false
      : baseIsComplete;

  const overlayMessages = useMemo(() => {
    if (!hasOverlay || overlayClearsSection) return [];

    const messages = [];

    if (overlayIsComplete) {
      if (overlayIsComplete.isRemoval) {
        messages.push(
          t("records.editor.task.ai.clear_completion", "AI will clear the completion status.")
        );
      } else if (typeof overlayIsComplete.value === "boolean") {
        messages.push(
          overlayIsComplete.value
            ? t("records.editor.task.ai.mark_complete", "AI will mark this task as complete.")
            : t("records.editor.task.ai.mark_incomplete", "AI will mark this task as incomplete.")
        );
      } else {
        messages.push(
          t(
            "records.editor.task.ai.update_completion",
            "AI suggested an update to completion status."
          )
        );
      }
    }

    if (overlayDueDate) {
      if (overlayDueDate.isRemoval) {
        messages.push(t("records.editor.task.ai.clear_due_date", "AI will clear the due date."));
      } else if (overlayDueDateInputValue) {
        const preview = formatDueDatePreview(overlayDueDateInputValue, {
          locale,
          t,
        });
        messages.push(
          preview
            ? t("records.editor.task.ai.suggest_due", {
                due: preview,
                defaultValue: `AI suggests due ${preview}.`,
              })
            : t("records.editor.task.ai.suggest_due_generic", "AI suggested a new due date.")
        );
      } else {
        messages.push(t("records.editor.task.ai.suggest_due_generic", "AI suggested a new due date."));
      }
    }

    if (overlayPriority) {
      if (overlayPriority.isRemoval) {
        messages.push(t("records.editor.task.ai.clear_priority", "AI will clear the priority."));
      } else if (displayedPriority) {
        const priorityLabel = t(
          `records.editor.task.priority.options.${displayedPriority}`,
          displayedPriority
        );
        messages.push(
          t("records.editor.task.ai.suggest_priority", {
            priority: priorityLabel,
            defaultValue: `AI suggests setting priority to ${priorityLabel}.`,
          })
        );
      } else {
        messages.push(
          t("records.editor.task.ai.update_priority", "AI suggested updating the priority.")
        );
      }
    }

    return messages;
  }, [
    hasOverlay,
    overlayClearsSection,
    overlayIsComplete,
    overlayDueDate,
    overlayPriority,
    overlayDueDateInputValue,
    displayedPriority,
    locale,
    t,
  ]);

  // Sync with parent state when dueDate prop changes (only when no overlay)
  useEffect(() => {
    if (hasOverlay) {
      return;
    }

    const nextDueDate = coerceDateInstance(dueDate);
    if (nextDueDate) {
      setIsDueDateEnabled(true);
      setLocalDueDateTime(formatLocalInputValue(nextDueDate));
      return;
    }

    if (dueDate === null) {
      setIsDueDateEnabled(false);
    }
  }, [dueDate, hasOverlay]);

  const handleToggleChange = (checked) => {
    if (hasOverlay) return;
    setIsDueDateEnabled(checked);

    if (checked) {
      // Enable: use existing date or smart default
      const fallback = coerceDateInstance(dueDate);
      const stored = localDueDateTime
        ? new Date(localDueDateTime)
        : null;
      const hasStoredValue =
        stored && Number.isFinite(stored.getTime());
      const dateTimeToSet = fallback
        ? formatLocalInputValue(fallback)
        : hasStoredValue
        ? localDueDateTime
        : formatLocalInputValue(getSmartDefault());
      setLocalDueDateTime(dateTimeToSet);

      // Convert to Date object for backend
      const dateObj = new Date(dateTimeToSet);
      onUpdate("dueDate", dateObj);
    } else {
      // Disable: clear the date
      onUpdate("dueDate", null); // This will trigger field deletion
    }
  };

  const handleDateTimeChange = (e) => {
    if (hasOverlay) return;
    const newDateTime = e.target.value;
    setLocalDueDateTime(newDateTime);

    // Convert to Date object for backend
    const dateObj = new Date(newDateTime);
    onUpdate("dueDate", dateObj);
  };

  const handlePriorityChange = (event) => {
    if (hasOverlay) return;
    const nextValue = event.target.value;
    onUpdate("priority", nextValue || null);
  };

  const handleCompletionChange = (event) => {
    if (hasOverlay) return;
    onUpdate("isComplete", event.target.checked);
  };

  return (
    <div
      className={`${styles.section} ${styles.active} ${
        hasOverlay ? styles.showingSuggestions : ""
      }`}
    >
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <svg
            className={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
          </svg>
          <span>{t("records.editor.task.title", "Task Details")}</span>
        </div>
        <button
          className={styles.deleteButton}
          onClick={onDelete}
          aria-label={t(
            "records.editor.task.remove_attributes_aria",
            "Remove task attributes"
          )}
          disabled={hasOverlay}
          type="button"
          title={t("records.editor.task.remove_attributes_aria", "Remove task attributes")}
        >
          ×
        </button>
      </div>

      {hasOverlay && (overlayClearsSection || overlayMessages.length > 0) && (
        <div
          className={
            overlayClearsSection
              ? styles.overlayRemovalBanner
              : styles.overlayBanner
          }
          role="status"
          aria-live="polite"
        >
          {overlayClearsSection ? (
            <p>{t("records.editor.task.ai.clear_task", "AI will clear this task.")}</p>
          ) : (
            overlayMessages.map((message, index) => (
              <p key={index}>{message}</p>
            ))
          )}
        </div>
      )}

      {!overlayClearsSection && (
        <>
          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel} htmlFor="taskCompleteToggle">
              <input
                id="taskCompleteToggle"
                type="checkbox"
                className={styles.checkbox}
                checked={displayedIsComplete}
                onChange={handleCompletionChange}
                disabled={hasOverlay}
              />
              {t("records.editor.task.mark_complete", "Mark as complete")}
            </label>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.labelRow}>
              <label className={styles.label} htmlFor="dueDateToggle">
                {t("records.editor.task.due_date_label", "Due Date")}
              </label>
              <label
                className={styles.toggle}
                aria-label={t("records.editor.task.toggle_due_date_aria", "Toggle due date")}
              >
                <input
                  type="checkbox"
                  id="dueDateToggle"
                  role="switch"
                  aria-checked={dueDateToggleChecked}
                  checked={dueDateToggleChecked}
                  onChange={(e) => handleToggleChange(e.target.checked)}
                  className={styles.toggleInput}
                  aria-label={t("records.editor.task.toggle_due_date_aria", "Toggle due date")}
                  disabled={hasOverlay}
                />
                <span className={styles.toggleSlider} aria-hidden="true"></span>
              </label>
            </div>

            {dueDateToggleChecked && (
              <div className={styles.dateTimeWrapper}>
                <input
                  type="datetime-local"
                  id="dueDateTime"
                  className={styles.dateTimeInput}
                  value={displayedDueDateTime}
                  onChange={handleDateTimeChange}
                  aria-label={t(
                    "records.editor.task.due_date_time_aria",
                    "Due date and time"
                  )}
                  disabled={hasOverlay}
                />
                {dueDatePreviewText && (
                  <span className={styles.datePreview}>
                    {dueDatePreviewText}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="priority">
              {t("records.editor.task.priority.label", "Priority")}
            </label>
            <select
              id="priority"
              className={styles.input}
              value={displayedPriority || ""}
              onChange={handlePriorityChange}
              aria-label={t("records.editor.task.priority.aria", "Task priority")}
              disabled={hasOverlay}
            >
              <option value="">
                {t("records.editor.task.priority.options.none", "None")}
              </option>
              <option value="high">
                {t("records.editor.task.priority.options.high", "High")}
              </option>
              <option value="medium">
                {t("records.editor.task.priority.options.medium", "Medium")}
              </option>
              <option value="low">
                {t("records.editor.task.priority.options.low", "Low")}
              </option>
            </select>
          </div>
        </>
      )}
    </div>
  );
}
